import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateProduct,
  CreateStockMovement,
  CreateSupplier,
  Product,
  ProductKind,
  ProductListQuery,
  StockLevel,
  StockMovement,
  StockMovementListQuery,
  Supplier,
  UpdateProduct,
  UpdateSupplier,
} from '@salonomy/types';
import { api, expectOk, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS7 — stock, products & suppliers (the inventory module). TanStack Query over the generated client,
 * mirroring data/vouchers.ts (paged reads + keepPreviousData) and data/staff.ts (create/edit/soft-
 * deactivate mutations + body-as-never + invalidate).
 *
 * HONESTY (non-negotiable): on-hand and stock VALUATION are SERVER-DERIVED — `product.onHand` /
 * `level.onHand` are a SUM(qty_delta) the API computes, and `level.stockValuePence` is the server's
 * figure (SignedPence — it can be NEGATIVE if a product is oversold). The UI DISPLAYS these; it never
 * computes or persists a running balance in JS. Stock is moved ONLY by an append-only MOVEMENT: there
 * is no PATCH/DELETE on a movement, and PATCH /products edits ATTRIBUTES ONLY (never quantity). A
 * correction is a NEW count_correction movement (signed delta), never an edit. So after ANY movement
 * write we invalidate the products list, the stock levels AND the movement ledger together — every
 * derived on-hand re-reads from the server.
 *
 * Movements are idempotent on an optional client `idempotencyKey`: the caller mints it once per intent
 * and gates the button on `isPending`, so a double-tap is a safe replay that moves no stock (the server
 * returns 200 with the original movement instead of 201). The server derives qtyDelta's SIGN by kind
 * (receive → +abs, waste → -abs, count_correction → the signed value verbatim). Two kinds are NOT
 * offered by the manual form: 'sell' (server-forbidden — a sale posts through the till) and 'return'
 * (RESERVED for the till's refund restock, which ADDS to on-hand). A supplier send-back is recorded as
 * a waste or a negative count_correction, so the manual form offers receive | waste | count_correction.
 */

export const PRODUCTS_PAGE = 50;
export const MOVEMENTS_PAGE = 50;

export interface ProductPage {
  data: Product[];
  pagination: { limit: number; offset: number; total: number };
}

export interface MovementPage {
  data: StockMovement[];
  pagination: { limit: number; offset: number; total: number };
}

/** The filter half of the movement-ledger query (the hook adds limit/offset). */
export type MovementFilter = Pick<StockMovementListQuery, 'productId' | 'kind' | 'from' | 'to'>;

// ── Reads ──────────────────────────────────────────────────────────────────────────

/**
 * The product catalogue — paged + filterable by free text / kind / barcode / low-stock / active. The
 * list endpoints return 200 with an empty page when there is no data, so there is no 404 to map; a
 * genuine error surfaces via `isError`, which the panel renders honestly. `keepPreviousData` keeps the
 * table steady while a filter or page changes.
 */
export function useProducts(query: Omit<ProductListQuery, 'limit' | 'offset'> = {}, offset = 0) {
  return useQuery({
    queryKey: qk.products(query, offset),
    queryFn: async (): Promise<ProductPage> =>
      unwrap(
        await api.GET('/v1/products', {
          params: { query: { ...query, limit: PRODUCTS_PAGE, offset } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/**
 * One product (carries the server's computed `onHand`). Gated on an id so a closed modal doesn't fire.
 * Currently UNUSED by the rendered UI — kept as a deliberate data-layer export for a future
 * product-detail view; its `qk.product(id)` key is already written by useUpdateProduct's setQueryData.
 */
export function useProduct(id: string | null) {
  return useQuery({
    queryKey: id ? qk.product(id) : ['product', 'none'],
    enabled: Boolean(id),
    queryFn: async (): Promise<Product> =>
      unwrap(await api.GET('/v1/products/{id}', { params: { path: { id: id! } } })),
  });
}

/** The supplier list (for the suppliers panel + the product form's supplier picker). */
export function useSuppliers() {
  return useQuery({
    queryKey: qk.suppliers,
    queryFn: async (): Promise<Supplier[]> => unwrap(await api.GET('/v1/suppliers')),
  });
}

/**
 * Per-active-product stock levels: onHand, the low-stock flag, and `stockValuePence` (SignedPence) per
 * row. The page sums these into a footer valuation total — but never assumes the sum is non-negative.
 * Reads live (staleTime 0): a valuation is a money-adjacent server figure, re-read after any movement.
 */
export function useStockLevels(query: { kind?: ProductKind; lowStockOnly?: boolean } = {}) {
  return useQuery({
    queryKey: qk.stockLevels(query.kind ?? null, query.lowStockOnly ?? null),
    staleTime: 0,
    queryFn: async (): Promise<StockLevel[]> =>
      unwrap(
        await api.GET('/v1/stock/levels', {
          params: { query: { kind: query.kind, lowStockOnly: query.lowStockOnly } },
        }),
      ),
  });
}

/**
 * The append-only movement LEDGER — paged + filterable by product / kind / date window. READ-ONLY:
 * there is no edit/delete of a movement (a correction is a new count_correction). `keepPreviousData`
 * keeps the ledger steady while a filter or page changes.
 */
export function useStockMovements(filter: MovementFilter = {}, offset = 0) {
  return useQuery({
    queryKey: qk.stockMovements(filter, offset),
    queryFn: async (): Promise<MovementPage> =>
      unwrap(
        await api.GET('/v1/stock/movements', {
          params: { query: { ...filter, limit: MOVEMENTS_PAGE, offset } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

// ── Mutations (manager + stock:write on the API) ────────────────────────────────────

/**
 * Create a product. `openingStock > 0` makes the server write a 'receive' movement, so a create can
 * change on-hand — invalidate levels + movements as well as the list.
 */
export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    // The generated body type over-requires default-valued fields (kind/costPence/pricePence/vatable/
    // trackStock/openingStock); cast at the boundary — the server re-validates against the same Zod.
    mutationFn: async (body: CreateProduct): Promise<Product> =>
      unwrap(await api.POST('/v1/products', { body: body as never })),
    onSuccess: () => invalidateStock(qc),
  });
}

/**
 * Edit a product's ATTRIBUTES ONLY (name/kind/supplier/sku/barcode/cost/price/vatable/threshold/
 * trackStock/active). NEVER quantity — stock moves only through a movement. `active: false` is the soft
 * deactivate, but we also expose useDeactivateProduct (DELETE) for the explicit archive control.
 */
export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: UpdateProduct }): Promise<Product> =>
      unwrap(
        await api.PATCH('/v1/products/{id}', {
          params: { path: { id: input.id } },
          body: input.body as never,
        }),
      ),
    onSuccess: (product) => {
      qc.setQueryData(qk.product(product.id), product);
      invalidateStock(qc);
    },
  });
}

/** Soft-delete a product (DELETE ⇒ active:false; no row is erased so it stays inspectable). 204. */
export function useDeactivateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> =>
      expectOk(await api.DELETE('/v1/products/{id}', { params: { path: { id } } })),
    onSuccess: () => invalidateStock(qc),
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateSupplier): Promise<Supplier> =>
      unwrap(await api.POST('/v1/suppliers', { body: body as never })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.suppliers }),
  });
}

export function useUpdateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    // `active: false` deactivates a supplier (soft — never erased; the list keeps it inspectable).
    mutationFn: async (input: { id: string; body: UpdateSupplier }): Promise<Supplier> =>
      unwrap(
        await api.PATCH('/v1/suppliers/{id}', {
          params: { path: { id: input.id } },
          body: input.body as never,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.suppliers }),
  });
}

/**
 * Post a stock movement. The manual form posts receive | waste | count_correction ('sell' is a 400
 * here; 'return' is reserved for the till's refund restock and is not offered manually). The caller
 * mints `idempotencyKey` once per intent and gates the button on `isPending`, so a double-tap is a safe
 * replay (200 with the original). The SERVER derives qtyDelta's sign by kind (receive → +abs, waste →
 * -abs) and preserves the typed sign only for count_correction; it is the source of truth. After ANY
 * movement the derived on-hand changes EVERYWHERE, so invalidate products + levels + movements together.
 */
export function useCreateStockMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateStockMovement): Promise<StockMovement> =>
      unwrap(await api.POST('/v1/stock/movements', { body: body as never })),
    onSuccess: () => invalidateStock(qc),
  });
}

// ── Invalidation helper ──────────────────────────────────────────────────────────────

/**
 * Any product create/edit/archive or any movement shifts a server-derived on-hand and the valuation, so
 * the catalogue list (`product.onHand`), the levels table and the movement ledger all re-read together.
 */
function invalidateStock(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['products'] });
  void qc.invalidateQueries({ queryKey: ['stock'] });
}
